import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { fetchEmployer } from 'syft-acp-core/store/employers/actions'
import { fetchWorker } from 'syft-acp-core/store/workers/actions'
import { fetchAgencyProfile } from 'syft-acp-core/actions/agency-profile'

type NamedRecord = { name?: string | null; company_name?: string | null }

type EntitySlice = {
  entityDetail?: Record<string, NamedRecord | undefined>
  entityMap?: Record<string, NamedRecord | undefined>
}

/**
 * An entity slice holds a record under `entityDetail` once it has been fetched on its own, and
 * under `entityMap` when it arrived as part of a list. Either is good enough for a name.
 */
const recordIn = (slice: EntitySlice | undefined, id?: number): NamedRecord | undefined =>
  id ? slice?.entityDetail?.[id] || slice?.entityMap?.[id] : undefined

/**
 * These hooks resolve a record's display name for a page title, fetching it when the store does
 * not already have it. They return undefined until it arrives -- react-helmet re-renders the
 * title when it does, so a caller can show the id in the meantime and never has to block.
 *
 * The fetch is guarded on the name being absent, and the effect only re-runs when the id or the
 * name changes, so a request that comes back empty is not retried in a loop.
 */
export const useEmployerName = (employerID?: number): string | undefined => {
  const dispatch = useDispatch()
  const name = useSelector(state => recordIn(state.employers, employerID)?.company_name)

  useEffect(() => {
    if (employerID && !name) {
      dispatch(fetchEmployer(employerID))
    }
  }, [dispatch, employerID, name])

  return name || undefined
}

export const useWorkerName = (workerID?: number): string | undefined => {
  const dispatch = useDispatch()
  const name = useSelector(state => recordIn(state.workers, workerID)?.name)

  useEffect(() => {
    if (workerID && !name) {
      dispatch(fetchWorker(workerID))
    }
  }, [dispatch, workerID, name])

  return name || undefined
}

export const useAgencyName = (agencyID?: number): string | undefined => {
  const dispatch = useDispatch()
  const name = useSelector(state => recordIn(state.agencyProfile, agencyID)?.name)

  useEffect(() => {
    if (agencyID && !name) {
      dispatch(fetchAgencyProfile({ agencyId: agencyID }))
    }
  }, [dispatch, agencyID, name])

  return name || undefined
}
